import type { VerifiedReference } from "@/lib/api/verify-references";

/**
 * DATE DE VERSION EN VIGUEUR — couche d'AFFICHAGE uniquement.
 *
 * Règle non négociable : la date affichée provient EXCLUSIVEMENT des
 * métadonnées officielles déjà renvoyées par la couche de vérification
 * (`officialTitle` / `officialDate`). Le modèle n'en produit jamais, et
 * lorsqu'aucune date n'est présente dans les métadonnées, on n'affiche RIEN.
 * L'omission est acceptable ; l'invention ne l'est pas.
 *
 * FORMAT RÉEL OBSERVÉ EN PRODUCTION (23/08/2026, appel direct à
 * `verify-references`) :
 *   officialTitle = "Article 1240 - (VIGUEUR) - Chapitre Ier : La
 *                    responsabilité extracontractuelle en général"
 *   officialTitle = "Article L271-1 - (VIGUEUR) - Section 1 : Dispositions
 *                    générales."
 *   officialDate  = absent pour les articles
 * Autrement dit, la base officielle renvoie un ÉTAT (`VIGUEUR` / `ABROGE`)
 * et non une date de version. On affiche donc cet état vérifié, sans jamais
 * fabriquer de date ; la date n'est affichée que si elle est réellement
 * présente dans les métadonnées.
 */

const MONTHS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

function formatIso(iso: string): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return undefined;
  const day = Number(m[3]);
  const month = Number(m[2]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  return `${day === 1 ? "1er" : day} ${MONTHS[month - 1]} ${m[1]}`;
}

function formatNumeric(d: string, mo: string, y: string): string | undefined {
  const day = Number(d);
  const month = Number(mo);
  if (month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  return `${day === 1 ? "1er" : day} ${MONTHS[month - 1]} ${y}`;
}

/**
 * Extrait la date de version en vigueur d'un article à partir des seules
 * métadonnées officielles. Renvoie `undefined` si la donnée est absente.
 */
export function inForceDate(
  ref: Pick<VerifiedReference, "kind" | "status" | "officialTitle" | "officialDate">,
): string | undefined {
  if (ref.kind !== "article") return undefined;
  // Une référence non retrouvée ou hors base n'a pas de version officielle.
  if (ref.status !== "confirmed" && ref.status !== "abrogated") return undefined;

  const title = (ref.officialTitle ?? "").replace(/\s+/g, " ");

  // « Version en vigueur depuis le 1er octobre 2016 », « en vigueur au 19/06/2008 »
  const textual = /(?:en\s+vigueur|modifi[ée]e?|version)\s*(?:depuis|[àa]\s+compter\s+du|au|le)?\s*(?:le\s+)?(\d{1,2})(?:er)?\s+([a-zàâçéèêëîïôûùüÿœ]+)\s+(\d{4})/i.exec(title);
  if (textual) {
    const idx = MONTHS.findIndex((m) => m.toLowerCase() === textual[2].toLowerCase());
    if (idx >= 0) return formatNumeric(textual[1], String(idx + 1), textual[3]);
  }

  const numeric = /(?:en\s+vigueur|modifi[ée]e?|version)\s*(?:depuis|[àa]\s+compter\s+du|au|le)?\s*(?:le\s+)?(\d{2})\/(\d{2})\/(\d{4})/i.exec(title);
  if (numeric) return formatNumeric(numeric[1], numeric[2], numeric[3]);

  const iso = /(?:en\s+vigueur|version)[^\d]{0,20}(\d{4}-\d{2}-\d{2})/i.exec(title);
  if (iso) return formatIso(iso[1]);

  if (ref.officialDate) return formatIso(ref.officialDate);

  return undefined;
}

/**
 * État de version tel que renvoyé par la base officielle : `(VIGUEUR)` ou
 * `(ABROGE)` dans `officialTitle`. C'est le SEUL marqueur réellement présent
 * en production pour les articles.
 */
export function inForceStatus(
  ref: Pick<VerifiedReference, "kind" | "status" | "officialTitle">,
): "vigueur" | "abroge" | undefined {
  if (ref.kind !== "article") return undefined;
  if (ref.status !== "confirmed" && ref.status !== "abrogated") return undefined;
  const title = (ref.officialTitle ?? "").toUpperCase();
  // CORRECTIF QA 25/08/2026 — `ABROGE_DIFF` signifie « en vigueur, abrogation
  // différée » : le texte s'applique toujours. L'ancien test par préfixe
  // (`/ABROG/`) le classait « abrogé », d'où deux étiquettes contradictoires
  // affichées côte à côte sur la même référence.
  if (/\(\s*ABROGE_DIFF\s*\)/.test(title)) return "vigueur";
  // `VIGUEUR_DIFF` : entrée en vigueur différée — ni en vigueur ni abrogé.
  // On n'affiche rien plutôt que d'affirmer un état inexact.
  if (/\(\s*VIGUEUR_DIFF\s*\)/.test(title)) return undefined;
  if (/\(\s*VIGUEUR[^)]*\)/.test(title)) return "vigueur";
  if (/\(\s*ABROG[^)]*\)/.test(title)) return "abroge";
  return undefined;
}

/** Libellé prêt à afficher, ou `undefined` si la métadonnée manque. */
export function inForceLabel(
  ref: Pick<VerifiedReference, "kind" | "status" | "officialTitle" | "officialDate">,
): string | undefined {
  const state = inForceStatus(ref);
  // Statut incertain (p. ex. entrée en vigueur différée) : aucune étiquette.
  if (!state && /\(\s*(VIGUEUR_DIFF|SANS_ETAT)\s*\)/.test((ref.officialTitle ?? "").toUpperCase())) {
    return undefined;
  }
  const date = inForceDate(ref);
  if (date) {
    // La date seule ne doit pas contredire l'état officiel : on s'aligne sur
    // `inForceStatus` lorsqu'il a tranché.
    if (state === "vigueur") return `en vigueur depuis le ${date}`;
    if (state === "abroge") return `version du ${date}`;
    return ref.status === "abrogated" ? `version du ${date}` : `en vigueur depuis le ${date}`;
  }
  // Pas de date dans les métadonnées : on affiche l'état vérifié, sans date.
  if (state === "vigueur") return "version en vigueur";
  if (state === "abroge") return "version abrogée";
  return undefined;
}

